export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-neutral-900 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="p-1 rounded-lg">
                  <img src="/icon.svg" alt="AIBoard" className="h-8 w-8" />
                </div>
                <span className="ml-3 text-xl font-bold text-white">AIBoard</span>
              </div>
              <h1 className="text-2xl font-bold">Terms & Conditions</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="prose prose-lg max-w-none">
          <div className="bg-neutral-50 p-6 rounded-lg mb-8">
            <p className="text-sm text-neutral-600 mb-2">Last updated: {new Date().toLocaleDateString()}</p>
            <p className="text-neutral-700">
              Your access to and use of the Services is subject to your understanding and consent to the terms and conditions outlined below.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Main Services Agreement</h2>
            
            <p className="mb-4">
              <strong>PLEASE REVIEW THE TERMS IN THIS MAIN SERVICES AGREEMENT ("AGREEMENT") CAREFULLY.</strong> By accessing or using the Services, you are accepting to be bound to the terms of this Agreement. ONCE ACCEPTED, THESE TERMS OF SERVICE BECOME A BINDING LEGAL COMMITMENT BETWEEN YOU AND AIBOARD. IF YOU DO NOT AGREE TO THESE TERMS, YOU SHOULD NOT ACCEPT THEM, CREATE AN ACCOUNT, OR USE THE SERVICES.
            </p>

            <p className="mb-4">
              THE SERVICES ARE INTENDED FOR BUSINESS USE OR USE IN CONNECTION WITH AN INDIVIDUAL'S TRADE, CRAFT, OR PROFESSION ONLY. You must be 18 years or older to use the Services.
            </p>

            <p className="mb-4">
              If you have a separate written agreement with AIBoard for your use of the Services, the terms in this Agreement will not apply to you, unless that written agreement does not cover a particular Service, in which case, this Main Services Agreement will apply to your use of that particular Service.
            </p>

            <p className="mb-4">
              This Agreement sets forth the terms for your use of the Services and is effective as of the date you accept or otherwise agree to the terms of this Agreement ("Effective Date"). This Agreement is between AIBoard and its affiliated entities (collectively "AIBoard" or "we") and you or the organization on whose behalf you are accepting or otherwise agreeing to the terms of this Agreement ("you", "your", "yours", or "Customer").
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <p className="text-sm">
                <strong>IMPORTANT:</strong> PLEASE REVIEW THE ARBITRATION PROVISIONS SET FORTH IN SECTION 16 BELOW CAREFULLY, AS IT WILL REQUIRE YOU TO RESOLVE DISPUTES WITH AIBOARD ON AN INDIVIDUAL BASIS THROUGH FINAL AND BINDING ARBITRATION.
              </p>
            </div>

            <p className="mb-4">
              THE TERMS OF THIS AGREEMENT MAY CHANGE. WE WILL MAKE COMMERCIALLY REASONABLE EFFORTS TO LET YOU KNOW AT LEAST THIRTY (30) DAYS BEFORE WE MAKE ANY SIGNIFICANT CHANGES THAT IMPACT YOU OR YOUR USE OF OUR SERVICES UNLESS WE ARE UNABLE TO BECAUSE OF CHANGES IN LAWS, REGULATIONS, OR CARRIER REQUIREMENTS.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">1. Acceptance of the Terms</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>By using or accessing any part of the Services, you agree to the terms of this Agreement, our Privacy Policy, and any other policies or notices posted by us on our website or through the Services (collectively, the "Terms").</li>
              <li>AIBoard's Privacy Policy covers how AIBoard collects, uses, shares, and stores personal information of users. If you do not agree to or understand AIBoard's Privacy Policy, including any changes, you are not authorized to use AIBoard's Services.</li>
              <li>The individual accepting the Terms represents that they have the authority to bind the company or legal entity to the Terms.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">2. Definitions</h2>
            <div className="space-y-3">
              <div>
                <strong>"Authorized User"</strong> means an individual subscriber or the agents, representatives, partners, members, employees, temporary employees, and independent contractors of an organization which have been listed in an Order Form or otherwise approved by AIBoard in writing, and for which access to the Services has been purchased.
              </div>
              <div>
                <strong>"Confidential Information"</strong> means all non-public information that a party designates as being confidential, or which, under the circumstances of disclosure ought to be treated as confidential.
              </div>
              <div>
                <strong>"Content"</strong> means information made available to you through the Services via AIBoard, other users, or one or more of AIBoard's third-party service providers.
              </div>
              <div>
                <strong>"Services"</strong> means any website or web-based, mobile, or offline product, feature, software, or service provided by AIBoard, including AIBoard's proprietary dashboard platform, analytics tools, reporting features, and other business intelligence solutions for which you have received access to.
              </div>
              <div>
                <strong>"User Content"</strong> means the content, including questions, answers, comments, documents, or other materials shared or uploaded by users of AIBoard's Services.
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">3. Use of the Services</h2>
            
            <h3 className="text-lg font-semibold mb-3">License Grant</h3>
            <p className="mb-4">
              Subject to your continued compliance with these Terms, AIBoard grants you a limited, revocable, non-exclusive, non-transferable right to access and use the Services for the Permitted Purpose during the Term.
            </p>

            <h3 className="text-lg font-semibold mb-3">The Services</h3>
            <p className="mb-4">
              AIBoard will use commercially reasonable efforts to make the Services available to you. The Services may be temporarily unavailable from time to time due to scheduled downtime or for reasons beyond AIBoard's reasonable control. AIBoard will not be liable for unavailability of the Services.
            </p>

            <h3 className="text-lg font-semibold mb-3">Use Restrictions</h3>
            <p className="mb-2">You will not use the Services for any purpose beyond the Permitted Use. Without limiting the foregoing, you will not:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Use the Services in any way that AIBoard determines to be competitive with AIBoard</li>
              <li>Violate any law or regulation, including any applicable export control laws and privacy laws</li>
              <li>Copy, modify, enhance, adapt, or otherwise create derivative works of the Services</li>
              <li>Redistribute, frameset, transmit, share or broadcast any part of Services</li>
              <li>Rent, lease, sell, provide access to or sublicense any element of the Services to a third party</li>
              <li>Reverse engineer, disassemble, decompile, decode, adapt, or otherwise attempt to derive or gain access to data made available as part of the Services</li>
              <li>Remove any proprietary or other notices contained in the Services</li>
              <li>Use the Services to create, develop, produce, or market a substantially similar product or Services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">4. Content</h2>
            
            <h3 className="text-lg font-semibold mb-3">User Generated Content</h3>
            <p className="mb-4">
              Some Services allow you and other third parties to disseminate content through the Services. This data is referred to as "User Content". You understand that any User Content you submit may be seen by third parties.
            </p>

            <h3 className="text-lg font-semibold mb-3">Your Submission of User Content</h3>
            <p className="mb-4">
              Between you and AIBoard, you own the User Content you submit or post to the Services, but grant AIBoard a worldwide, perpetual, transferrable, royalty-free right to use, translate, reproduce, host, publicly display, publish, publicly perform, adapt, modify, distribute, promote and otherwise exploit in any manner your User Content for any purpose related to the Services.
            </p>

            <h3 className="text-lg font-semibold mb-3">Content You View</h3>
            <p className="mb-4">
              AIBOARD HAS NO DUTY TO PUBLISH, MONITOR, OR REVIEW ANY CONTENT AND YOUR ACCESS TO OR USE OF ANY CONTENT IS AT YOUR OWN RISK. YOU ACKNOWLEDGE THAT ALL CONTENT IS PROVIDED FOR YOUR INFORMATIONAL PURPOSES ONLY AND YOU ARE SOLELY RESPONSIBLE FOR THE BUSINESS DECISIONS YOU MAKE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">5. Term and Termination</h2>
            
            <h3 className="text-lg font-semibold mb-3">Term</h3>
            <p className="mb-4">
              These Terms take effect on the Effective Date and will remain in effect until all applicable Order Forms have expired or been terminated. Subscriptions for each Service will automatically renew unless either party provides notice of non-renewal at least thirty (30) days prior to commencement of the next Renewal Term.
            </p>

            <h3 className="text-lg font-semibold mb-3">Termination</h3>
            <p className="mb-4">
              A party may terminate these Terms for cause upon written notice to the other party upon a material breach if such breach remains uncured within thirty (30) days from the other party's receipt of said notice.
            </p>

            <h3 className="text-lg font-semibold mb-3">Effect of Termination</h3>
            <p className="mb-4">
              AIBoard is not a data storage solution. Upon expiration or termination of these Terms, you will immediately discontinue use of the Services and AIBoard may delete all data in its possession.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">6. Intellectual Property Ownership</h2>
            <p className="mb-4">
              AIBoard and its licensors own all rights, title, and interest in and to the Services, including all copyright, trademark, trade secret, and patent rights, and reserves all such intellectual property rights. These Terms do not grant you any intellectual property rights in or to the Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">7. Limitations of Liability</h2>
            
            <h3 className="text-lg font-semibold mb-3">Complete Limitation of Liability</h3>
            <p className="mb-4">
              <strong>IN NO EVENT SHALL AIBOARD BE LIABLE FOR ANY DAMAGES WHATSOEVER</strong>, INCLUDING BUT NOT LIMITED TO ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, PUNITIVE, EXEMPLARY, OR OTHER DAMAGES, INCLUDING LOSS OF REVENUE, DATA, OR PROFITS, BUSINESS INTERRUPTION, OR ANY OTHER COMMERCIAL DAMAGES OR LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OR INABILITY TO USE THE SERVICES, REGARDLESS OF THE LEGAL THEORY UNDER WHICH SUCH LIABILITY MAY BE ASSERTED, EVEN IF AIBOARD HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>

            <h3 className="text-lg font-semibold mb-3">No Financial Responsibility</h3>
            <p className="mb-4">
              AIBOARD'S AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF THE SERVICES SHALL BE ZERO ($0). AIBOARD SHALL NOT BE RESPONSIBLE FOR ANY LOSSES, DAMAGES, OR COSTS OF ANY KIND UNDER ANY CIRCUMSTANCES.
            </p>

            <h3 className="text-lg font-semibold mb-3">Disclaimer of Warranties</h3>
            <p className="mb-4">
              YOUR USE OF THE SERVICES AND ANY CONTENT RECEIVED THROUGH THE SERVICES IS ENTIRELY AT YOUR OWN RISK. ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NONINFRINGEMENT. AIBOARD MAKES NO REPRESENTATIONS OR WARRANTIES ABOUT THE ACCURACY, RELIABILITY, COMPLETENESS, OR TIMELINESS OF THE SERVICES.
            </p>

            <h3 className="text-lg font-semibold mb-3">User Acknowledgment</h3>
            <p className="mb-4">
              BY USING THE SERVICES, YOU ACKNOWLEDGE AND AGREE THAT AIBOARD SHALL HAVE NO LIABILITY TO YOU FOR ANY LOSSES OR DAMAGES ARISING FROM YOUR USE OF THE SERVICES, AND YOU WAIVE ANY CLAIMS AGAINST AIBOARD RELATED TO SUCH USE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">8. Privacy and Data Protection</h2>
            <p className="mb-4">
              You acknowledge and agree that you will comply with all applicable data protection laws and regulations. AIBoard's Privacy Policy governs the collection, use, and disclosure of personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">9. Governing Law</h2>
            <p className="mb-4">
              These Terms will be governed by and construed under applicable laws without regard to conflicts of law rules. Any disputes arising from these Terms shall be resolved through binding arbitration.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">10. Contact Information</h2>
            <p className="mb-4">
              If you have any questions about these Terms, please contact us through our website or support channels.
            </p>
          </section>

          <div className="bg-neutral-50 p-6 rounded-lg mt-8">
            <p className="text-sm text-neutral-600">
              This document constitutes the entire agreement between you and AIBoard regarding your use of the Services. 
              By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
} 